import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { beforeAfterCategories } from '../mock';
import { useLang } from '../i18n/LanguageContext';

const BeforeAfter = () => {
  const { t } = useLang();
  const [active, setActive] = useState(beforeAfterCategories[0].id);
  const [offset, setOffset] = useState(0);
  const [perView, setPerView] = useState(5);
  const current = beforeAfterCategories.find((c) => c.id === active);
  const images = current.images;

  // Responsive perView: 1 on mobile, 2 on tablet, 3 on small desktop, 5 on wide.
  React.useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      if (w < 640) setPerView(1);
      else if (w < 900) setPerView(2);
      else if (w < 1200) setPerView(3);
      else setPerView(5);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  const next = () => setOffset((o) => Math.min(o + 1, Math.max(0, images.length - perView)));
  const prev = () => setOffset((o) => Math.max(o - 1, 0));

  const switchTab = (id) => {
    setActive(id);
    setOffset(0);
  };

  // Width per slide: percentage of viewport so a single slide fills ~85% on mobile
  // (with a peek of the next), 50% on tablet, 33% / 20% on larger screens.
  const slideWidthPct = 100 / perView;
  const peek = perView === 1 ? 0.92 : 1; // shrink slightly on mobile to peek next
  const widthCalc = `calc(${slideWidthPct * peek}% - 16px)`;

  return (
    <section className="py-16 sm:py-24 md:py-32 overflow-hidden relative">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 text-center">
        <h2 className="font-serif-display text-3xl sm:text-4xl md:text-6xl mb-4 leading-tight">
          <span className="peach-text block">Your Transformation Journey</span>
          <span className="text-[#ffffff]">{t('ba.title.italic')}</span>
        </h2>
        <p className="text-[#ffffff]/70 text-sm sm:text-base max-w-2xl mx-auto mb-8 sm:mb-12 mt-4 px-2">
          For the price of one tooth in your country, get your entire mouth treated at Gold Care Esthetic. Solve dental issues for good with implants.
        </p>

        {/* Tabs — horizontally scrollable on mobile so all 4 fit comfortably */}
        <div className="mb-10 sm:mb-16 overflow-x-auto -mx-5 px-5 sm:mx-0 sm:px-0 scrollbar-hide">
          <div className="inline-flex items-center gap-1 bg-[#124D3A]/80 border border-[#D4AF37]/15 rounded-full p-1.5 whitespace-nowrap">
          {beforeAfterCategories.map((c) => (
            <button
              key={c.id}
              onClick={() => switchTab(c.id)}
              data-testid={`ba-tab-${c.id}`}
              className={`relative px-6 py-2.5 rounded-full text-sm transition-all ${
                active === c.id
                  ? 'bg-[#D4AF37] text-[#0B3D2E] font-semibold'
                  : 'text-[#ffffff]/80 hover:text-[#D4AF37]'
              }`}
            >
              {t(c.i18nKey, c.label)}
            </button>
          ))}
          </div>
        </div>
      </div>

      {/* Carousel */}
      <div className="relative max-w-[1440px] mx-auto px-5 sm:px-6">
        <div className="overflow-hidden">
          <div
            className="flex gap-4 sm:gap-5 transition-transform duration-700 ease-out"
            style={{ transform: `translateX(calc(-${offset} * (${slideWidthPct}% + 16px)))` }}
          >
            {images.map((img, idx) => {
              const isActive = perView >= 5
                ? idx === offset + 2
                : perView >= 3
                ? idx === offset + 1
                : idx === offset;
              return (
                <div
                  key={idx}
                  className={`flex-shrink-0 aspect-square rounded-2xl overflow-hidden relative card-hover group ${
                    isActive ? 'scale-[1.02] z-10' : 'opacity-80'
                  }`}
                  style={{ width: widthCalc }}
                >
                  <img
                    src={img}
                    alt={`${t(current.i18nKey, current.label)} before and after ${idx + 1}`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                  <div className="absolute top-3 left-3 right-3 flex justify-between text-[11px] tracking-[0.15em] uppercase text-white/90 font-medium">
                    <span className="px-2.5 py-1 bg-[#0B3D2E]/70 rounded-full backdrop-blur">{t('ba.before', 'Before')}</span>
                    <span className="px-2.5 py-1 bg-[#D4AF37]/80 rounded-full text-[#0B3D2E]">{t('ba.after', 'After')}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Nav buttons */}
        <button
          onClick={prev}
          className="absolute left-1/2 -translate-x-[60px] -bottom-16 w-12 h-12 rounded-full bg-[#124D3A] border border-[#D4AF37]/30 flex items-center justify-center hover:bg-[#D4AF37] hover:text-[#0B3D2E] transition-colors text-[#ffffff]"
          aria-label="Previous"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={next}
          className="absolute left-1/2 translate-x-[12px] -bottom-16 w-12 h-12 rounded-full bg-[#D4AF37] text-[#0B3D2E] flex items-center justify-center hover:bg-[#C49B2C] transition-colors"
          aria-label="Next"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
      <div className="h-20" />
    </section>
  );
};

export default BeforeAfter;
